import { useCallback, useRef, useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { makeAskStreamSynthesisSession } from "../../domain/use-cases/ask-stream-synthesis-session"
import { httpSynthesisSessionRepository } from "../../data/repositories/synthesis-session-repository"
import type {
  AskStreamPhase,
  SynthesisAskResult,
  SynthesisSession,
} from "../../domain/entities/synthesis-session"

const askStreamApi = makeAskStreamSynthesisSession(httpSynthesisSessionRepository)

const sessionKey = (sessionId: string) => ["synthesis-sessions", sessionId]

/**
 * Map a fine-grained backend `status` stage (constants.py STATUS_*) to the
 * coarse phase the UI labels. Unknown stages fall back to the current phase.
 */
const STAGE_TO_PHASE: Record<string, AskStreamPhase> = {
  rewriting_question: "analyzing",
  analyze_intent: "analyzing",
  extract_important_keywords: "analyzing",
  replanning: "planning",
  gathering_info: "gathering_info",
  searching_chunks: "gathering_info",
  reranking_documents: "reranking",
  extracting_parent: "reading_sources",
  // Tool-internal finalize (compressing retrieved chunks). The real final
  // answer synthesis arrives as the bare `synthesize_answer` EVENT, which owns
  // the "Composing answer…" label — so this in-gather stage reads as reading.
  synthesize_answer: "reading_sources",
  no_results: "retrying",
}

const isAbortError = (err: unknown): boolean =>
  err instanceof DOMException && err.name === "AbortError"

export type AskStreamStartArgs = {
  sessionId: string
  content: string
  /**
   * Conversation up to and including the just-sent user message. Used as the
   * base when committing the final assistant reply to the React Query cache.
   */
  sessionForMerge: SynthesisSession
  onDone?: (session: SynthesisSession, result: SynthesisAskResult) => void
  onError?: (error: Error) => void
}

export type UseAskStreamSynthesisSessionResult = {
  start: (args: AskStreamStartArgs) => void
  cancel: () => void
  isStreaming: boolean
  phase: AskStreamPhase | null
  streamedText: string
  /** True once the server sends a `working` heartbeat, i.e. the current stage
   * has stalled for >1 heartbeat interval. Reset on the next real event. */
  isLongWait: boolean
  /** Sticky notice from a `degraded` status event; cleared when the turn ends. */
  notice: string | null
  /** Latest retrieval-plan summary from a `plan` event; ephemeral, cleared when the turn ends. */
  planSummary: string | null
}

export const useAskStreamSynthesisSession =
  (): UseAskStreamSynthesisSessionResult => {
    const qc = useQueryClient()
    const [isStreaming, setIsStreaming] = useState(false)
    const [phase, setPhase] = useState<AskStreamPhase | null>(null)
    const [streamedText, setStreamedText] = useState("")
    const [isLongWait, setIsLongWait] = useState(false)
    const [notice, setNotice] = useState<string | null>(null)
    const [planSummary, setPlanSummary] = useState<string | null>(null)
    const abortRef = useRef<AbortController | null>(null)

    const start = useCallback(
      ({
        sessionId,
        content,
        sessionForMerge,
        onDone,
        onError,
      }: AskStreamStartArgs) => {
        abortRef.current?.abort()
        const controller = new AbortController()
        abortRef.current = controller

        setIsStreaming(true)
        setPhase("analyzing")
        setStreamedText("")
        setIsLongWait(false)
        setNotice(null)
        setPlanSummary(null)

        let accumulated = ""

        const run = async () => {
          try {
            for await (const event of askStreamApi(
              sessionId,
              content,
              controller.signal,
            )) {
              if (controller.signal.aborted) return
              switch (event.type) {
                case "analyzing":
                case "planning":
                case "gathering_info":
                case "synthesize_answer":
                  setPhase(event.type)
                  setIsLongWait(false)
                  break
                case "plan":
                  setPlanSummary(event.summary)
                  setIsLongWait(false)
                  break
                case "answer":
                  accumulated += event.delta
                  setPhase("answering")
                  setStreamedText(accumulated)
                  setIsLongWait(false)
                  break
                case "status":
                  if (event.state === "degraded") {
                    // A step failed but the turn continues → sticky notice.
                    setNotice(
                      event.detail ??
                        "A retrieval step failed; the answer may be partial.",
                    )
                  } else if (event.heartbeat) {
                    // Keepalive on the SAME stage → the current stage has
                    // stalled. Keep its label; the UI only appends a hint.
                    setIsLongWait(true)
                  } else {
                    // Fresh retrieval sub-stage = real progress. Advance the
                    // phase label and clear the stalled flag.
                    const mapped = STAGE_TO_PHASE[event.stage]
                    if (mapped) setPhase(mapped)
                    setIsLongWait(false)
                  }
                  break
                case "done": {
                  const updated: SynthesisSession = {
                    ...sessionForMerge,
                    messages: [
                      ...sessionForMerge.messages,
                      {
                        id: `assistant-${Date.now()}`,
                        role: "assistant",
                        content: event.result.answer,
                        references: event.result.references,
                        followUpQuestions: event.result.followUpQuestions,
                        consulted: event.result.consulted,
                      },
                    ],
                  }
                  qc.setQueryData(sessionKey(sessionId), updated)
                  onDone?.(updated, event.result)
                  return
                }
                case "error":
                  throw new Error(event.message)
              }
            }
          } catch (err) {
            if (controller.signal.aborted || isAbortError(err)) return
            onError?.(err instanceof Error ? err : new Error(String(err)))
          } finally {
            if (abortRef.current === controller) {
              abortRef.current = null
            }
            setIsStreaming(false)
            setPhase(null)
            setStreamedText("")
            setIsLongWait(false)
            setNotice(null)
            setPlanSummary(null)
          }
        }

        void run()
      },
      [qc],
    )

    const cancel = useCallback(() => {
      abortRef.current?.abort()
      abortRef.current = null
      setIsStreaming(false)
      setPhase(null)
      setStreamedText("")
      setIsLongWait(false)
      setNotice(null)
      setPlanSummary(null)
    }, [])

    return { start, cancel, isStreaming, phase, streamedText, isLongWait, notice, planSummary }
  }
